// Timer used within the survey page that will automatically time out a user if they haven't responded in a while
import React, { useEffect, useState } from 'react';
import { Modal, Typography } from 'antd';
import { useIdleTimer } from 'react-idle-timer'; // for more details: https://idletimer.dev
import { verificationTokenAtom, updateSurveyAtom, decodedTokenAtom } from '../../../store';
import { useAtom } from 'jotai';
import { usePostQuery } from 'api/hooks/usePostQuery';
import { usePatchQuery } from 'api/hooks/usePatchQuery';
import { useDeactivateSession } from 'api/hooks/useDeactivateSession';
import '../modal.scss';

const { Paragraph, Title } = Typography;
interface TimerProps {
  timeOutInSeconds: number;
  showTimeOutPromptInSeconds: number;
  interactionId: number | null;
}

const IdleTimer: React.FC<TimerProps> = ({
  timeOutInSeconds,
  showTimeOutPromptInSeconds,
  interactionId,
}) => {
  const [surveyAtom] = useAtom(updateSurveyAtom);
  const [token] = useAtom(verificationTokenAtom);
  const [decodedToken] = useAtom(decodedTokenAtom);
  const staffMemberId = decodedToken?.staffMemberId;

  const deactivate = useDeactivateSession();

  // Staff and patient interactions live in separate tables, so keep-alive touches
  // need to target the correct one for the type of session that's active. The two
  // endpoints also differ in HTTP verb, so both hooks are called unconditionally
  // (per the rules of hooks) and the right exec function is picked below.
  const touchConfig = { headers: { Authorization: `Bearer ${token}` } };
  const { exec: execPatientTouch } = usePostQuery<void, void>(
    `interactions/${interactionId}/touch`,
    touchConfig,
  );
  const { exec: execStaffTouch } = usePatchQuery<void, void>(
    `staff_interactions/${interactionId}/touch`,
    touchConfig,
  );
  const execTouch = staffMemberId ? execStaffTouch : execPatientTouch;

  const timeout = timeOutInSeconds * 1000;
  const promptBeforeIdle = showTimeOutPromptInSeconds * 1000;
  const [, setRemaining] = useState<number>(timeout);
  const [remainingMinutes, setRemainingMinutes] = useState<number>(
    showTimeOutPromptInSeconds / 60,
  );
  const [remainingSeconds, setRemainingSeconds] = useState<string>('00');
  const [showInactivityPrompt, setShowInactivityPrompt]
    = useState<boolean>(false);

  const onIdle = () => {
    setShowInactivityPrompt(false);
    deactivate();
  };

  const onActive = () => {
    setShowInactivityPrompt(false);
    reactivateInteraction();
  };

  const onPrompt = () => {
    setShowInactivityPrompt(true);
  };

  const onAction = () => {
    keepAlive();
    reactivateInteraction();
  };

  const { getRemainingTime, activate } = useIdleTimer({
    onIdle,
    onActive,
    onPrompt,
    onAction,
    events: ['keydown', 'mousedown', 'touchstart'],
    debounce: 3000,
    timeout,
    promptBeforeIdle,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const remainingSeconds = Math.ceil(getRemainingTime() / 1000);
      setRemaining(remainingSeconds);

      const timeInMinutes = Math.floor(remainingSeconds / 60);
      const timeInSeconds = Math.floor(remainingSeconds - timeInMinutes * 60);

      if (timeInSeconds <= 0 || timeInSeconds > 59) {
        setRemainingSeconds('00');
      }
      else {
        setRemainingSeconds(`${timeInSeconds}`.padStart(2, '0'));
      }
      setRemainingMinutes(timeInMinutes);
    }, 500);

    return () => {
      clearInterval(interval);
    };
  });

  const reactivateInteraction = async () => {
    // if they are on the landing-page, they do not yet have an interactionId
    if (!interactionId || surveyAtom?.editStatus === 'closed') return;
    await execTouch();
  };

  const keepAlive = () => {
    activate();
  };

  const timeText = remainingMinutes > 0 ? 'minutes' : 'seconds';

  return (
    <Modal
      className="modal"
      closable={false}
      title="Your Session is About to End"
      open={showInactivityPrompt}
      onOk={keepAlive}
      onCancel={() => deactivate()}
      okText="Continue Session"
      cancelText="Log out"
      data-cy="survey_idle_timer"
    >
      <Paragraph style={{ fontSize: '14px' }}>
        You&lsquo;ve been inactive for a while.
        {'\n'}
        For your security
        we&lsquo;ll automatically sign you out
      </Paragraph>
      <Title className="notranslate" translate="no" level={4} style={{ marginTop: '-8px' }}>
        {remainingMinutes > 0 && `${remainingMinutes}:`}
        {remainingSeconds} {timeText}
      </Title>
    </Modal>
  );
};

export default IdleTimer;
